import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { css } from "@styled-system/css";
import React from "react";

import { Box, Flex, Skeleton, Text } from "~/components/blocks";
import { PharmacyProfileFragment, PublicAddress } from "~/graphql";

type Props = {
  fontColor?: "white";
  outline?: boolean;
  withContact?: boolean;
  pharmacy?: PharmacyProfileFragment;
};

const Root = styled(Flex)<{ fontColor?: string; outline?: boolean }>(
  ({ theme, fontColor }) =>
    css({
      alignItems: "center",
      color: fontColor === "white" ? theme.colors.text.white : undefined,
    }),
  ({ theme, outline }) =>
    outline &&
    css({
      margin: `${theme.space.l} 0`,
      border: theme.borders.default,
      borderRadius: theme.radii.default,
      padding: theme.space.l,
    }),
);

const Image = styled("img")(() =>
  css({
    width: "64px",
    height: "64px",
    objectFit: "contain",
  }),
);

const Info = styled("dl")();

const Name = styled("dt")(({ theme }) =>
  css({
    fontSize: theme.fontSizes.m,
    fontWeight: theme.fontWeights.bold,
  }),
);

const Address = styled("dd")(({ theme }) =>
  css({
    fontSize: theme.fontSizes.xs,
    marginTop: theme.space.s,
  }),
);

const Contact = styled("dd")(({ theme }) =>
  css({
    fontSize: theme.fontSizes.xs,
    marginTop: theme.space.s,
    "&::before": {
      content: '"連絡先："',
    },
  }),
);

const TelLink = styled("a")(({ theme }) =>
  css({
    color: theme.colors.text.default,
  }),
);

const ElectronicPrescriptionLabel = styled(Text)(({ theme }) =>
  css({
    alignItems: "center",
    padding: `${theme.space.xs} ${theme.space.m}`,
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.colorPallete.lightGreen,
    borderRadius: "4px",
    fontWeight: theme.fontWeights.bold,
    fontSize: theme.fontSizes.s,
  }),
);

const toAddress = (address: Partial<PublicAddress>) => {
  return `${address.prefecture}${address.city}${address.line}`;
};

export const PharmacyProfile = React.memo((props: Props) => {
  const { pharmacy } = props;
  const theme = useTheme();

  const address = pharmacy
    ? toAddress(pharmacy.organizationAddresses[0].address)
    : "";

  return (
    <Root fontColor={props.fontColor} outline={props.outline}>
      {pharmacy ? (
        <>
          <Image
            src={
              pharmacy.webBookingsSetting.logo
                ? pharmacy.webBookingsSetting.logo.url
                : process.env.defaultLogoPath
            }
          />
          <Box paddingX={theme.space.m}>
            <Info>
              <Name>{pharmacy.name}</Name>
              <Address>{address}</Address>
              {props.withContact && (
                <Contact>
                  <TelLink href={`tel:${pharmacy.phoneNumber}`}>
                    {pharmacy.phoneNumber}
                  </TelLink>
                </Contact>
              )}
              {pharmacy.webBookingsSetting.electronicPrescription && (
                <Box paddingTop={theme.space.s}>
                  <ElectronicPrescriptionLabel>
                    電子処方箋対応
                  </ElectronicPrescriptionLabel>
                </Box>
              )}
            </Info>
          </Box>
        </>
      ) : (
        <>
          <Skeleton height="64px" width="64px" />
          <Box paddingX={theme.space.m}>
            <Info>
              <Name>
                <Skeleton height="25px" width="200px" />
              </Name>
              <Address>
                <Skeleton height="20px" width="200px" />
              </Address>
              {props.withContact && <Skeleton height="20px" width="120px" />}
            </Info>
          </Box>
        </>
      )}
    </Root>
  );
});

PharmacyProfile.displayName = "PharmacyProfile";
